import React, { useMemo } from "react";
import "./templateSectionGridLoader.css";

export type TemplateSectionGridLoaderVariant = "shop" | "content";

export interface TemplateSectionGridLoaderProps {
  /** Tile count; defaults to two rows at the active column count. */
  count?: number;
  /** Shop desktop column override (3–5). Content uses the feed grid (5 desktop). */
  columns?: number;
  variant?: TemplateSectionGridLoaderVariant;
  device?: "desktop" | "tablet" | "mobile";
  className?: string;
  /**
   * `feed` — matches StayUpToDate embed (`RenderIndividualUI`): 2/3/5 cols,
   * 350px media height. `aspect` — legacy 4/5 card ratio (mock shop grid).
   */
  tileLayout?: "aspect" | "feed";
}

function columnsForDevice(
  device: "desktop" | "tablet" | "mobile",
  variant: TemplateSectionGridLoaderVariant,
  columns?: number,
  tileLayout: "aspect" | "feed" = "aspect",
): number {
  if (device === "mobile") return 2;
  if (device === "tablet") return 3;
  if (tileLayout === "feed") {
    return 5;
  }
  if (variant === "shop" && columns != null && columns >= 2 && columns <= 6) {
    return columns;
  }
  return 5;
}

function tileCountFor(cols: number, count?: number): number {
  if (count != null && count > 0) return count;
  return cols * 2;
}

/**
 * Foundation-style animated square grid for website template shop/content feeds.
 * Column count tracks the live feed grid (2 mobile / 3 tablet / 5 desktop, or shop columns).
 */
export function TemplateSectionGridLoader({
  count,
  columns,
  variant = "content",
  device = "desktop",
  className,
  tileLayout = "aspect",
}: TemplateSectionGridLoaderProps) {
  const cols = columnsForDevice(device, variant, columns, tileLayout);
  const tiles = tileCountFor(cols, count);

  const style = useMemo(
    () =>
      ({
        ["--wtgl-cols" as string]: String(cols),
      }) as React.CSSProperties,
    [cols],
  );

  const rootClass = [
    "mte-wtgl",
    variant === "shop" ? "mte-wtgl--shop" : "mte-wtgl--content",
    tileLayout === "feed" ? "mte-wtgl--feed-embed mte-wtgl--enter" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      style={style}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading posts"
    >
      {Array.from({ length: tiles }, (_, i) => (
        <div
          key={i}
          className="mte-wtgl-tile"
          style={{ ["--mte-wtgl-stagger" as string]: String(Math.min(i, 23)) }}
          aria-hidden
        />
      ))}
    </div>
  );
}

/** Alias used across StayUpToDate loaders and template chrome. */
export const WebsiteTemplateGridLoader = TemplateSectionGridLoader;

/** Props threaded from {@link RenderComponentLoader} on shop/content carousel tabs. */
export type WebsiteTemplateCarouselLoaderProps = {
  templatePageVariant?: string;
  templateDevice?: "desktop" | "tablet" | "mobile";
  templateGridColumns?: number;
  templateGridLoaderProps?: {
    variant: TemplateSectionGridLoaderVariant;
    device?: "desktop" | "tablet" | "mobile";
    columns?: number;
  } | null;
  count?: number;
};

/**
 * @deprecated Skeleton blocks removed — shop/content tabs use staggered tile fade-in instead.
 * Kept as a no-op so existing imports do not break.
 */
export function WebsiteTemplateCarouselLoader(
  _props?: WebsiteTemplateCarouselLoaderProps,
): React.ReactNode {
  return null;
}
